/**
 * Manually maintained controlModel members for ScopeScript Monaco completion.
 * Subset of Shared/computed.js and Shared/methods.js — edit this list directly.
 */

import { buildSignatureFromParameterNames } from './controlDesignerScopeScriptSignatureHelp.js';

/**
 * @typedef {Object} ControlModelMember
 * @property {string} name
 * @property {'function' | 'property'} kind
 * @property {string} [description]
 * @property {string[]} [parameters]
 */

/** @type {ControlModelMember[]} */
export const CONTROL_MODEL_MEMBERS = [
    {
        name: 'Name',
        kind: 'property',
        description: 'ControlData.Name of this control, or a fallback label.',
    },
    {
        name: 'Parent',
        kind: 'property',
        description: 'Nearest CallCorp parent control component.',
    },
    {
        name: 'Root',
        kind: 'property',
        description: 'Root application / page component.',
    },
    {
        name: 'Self',
        kind: 'property',
        description: 'Alias for the control component instance (this).',
    },
    {
        name: 'Location',
        kind: 'property',
        description: 'Dot-separated path describing this control in the component tree.',
    },
    {
        name: 'LocationBrief',
        kind: 'property',
        description: 'Shortened Location path for display and debugging.',
    },

    {
        name: 'ChildControlByName',
        kind: 'function',
        description: 'Find a child control by name.',
        parameters: ['name', 'ignoreErrors'],
    },
    {
        name: 'ChildControlByType',
        kind: 'function',
        description: 'Find the first child control matching a ControlType.',
        parameters: ['type', 'ignoreErrors'],
    },
    {
        name: 'ParentByName',
        kind: 'function',
        description: 'Walk up the tree to find a parent control by name.',
        parameters: ['name', 'ignoreerrors', 'debug'],
    },
    {
        name: 'Translate',
        kind: 'function',
        description: 'Translate display text using the platform translation service.',
        parameters: ['text', 'isToolTip'],
    },
    {
        name: 'ParamByName',
        kind: 'function',
        description: 'Resolve a parameter value by name from the control scope.',
        parameters: ['name', 'returnparent', 'suppresserror', 'log'],
    },
    {
        name: 'GenerateUUID',
        kind: 'function',
        description: 'Generate a new UUID string.',
        parameters: [],
    },
    {
        name: 'FindDesigner',
        kind: 'function',
        description: 'Find the Control Designer host component in the ancestor chain.',
        parameters: [],
    },
    {
        name: 'CallFunc',
        kind: 'function',
        description: 'Invoke a named function on this control with up to seven arguments.',
        parameters: ['name', 'a', 'b', 'c', 'd', 'e', 'f', 'g'],
    },
    {
        name: 'VarByName',
        kind: 'function',
        description: 'Find a variable by name.',
        parameters: ['name', 'returnparent', 'suppresserror'],
    },
    {
        name: 'ParamByName',
        kind: 'function',
        description: 'Find a parameter by name.',
        parameters: ['name', 'returnparent', 'suppresserror', 'log'],
    },
    {
        name: 'SourceData',
        kind: 'function',
        description: 'Locates source data from a repeating control, such as VerticalStack / HorizontalStack.',
        parameters: [],
    },
    {
        name: 'SourceKey',
        kind: 'function',
        description: 'Locates source index or key from a repeating control, such as VerticalStack / HorizontalStack.',
        parameters: [],
    },
    {
        name: 'RowData',
        kind: 'function',
        description: 'Locates row data from within a cell of a BasicGrid or InfiniteGrid.',
        parameters: [],
    },
    {
        name: 'RowIndex',
        kind: 'function',
        description: 'Locates row index from within a cell of a BasicGrid or InfiniteGrid.',
        parameters: [],
    },
    {
        name: 'EmitEvent',
        kind: 'function',
        description: 'Publishes a declared event to the parent control document which is using a reference to embed the current user control.',
        parameters: ['name', 'input'],
    },
];

const membersByName = new Map(
    CONTROL_MODEL_MEMBERS.map((member) => [member.name, member]),
);

/**
 * @returns {{ name: string, kind: string, runtimeSource: 'controlModel', description?: string, parameters?: string[] }[]}
 */
export function buildControlModelDotCompletions() {
    return CONTROL_MODEL_MEMBERS.map((member) => ({
        name: member.name,
        kind: member.kind === 'function' ? 'function' : 'variable',
        runtimeSource: 'controlModel',
        description: member.description,
        parameters: member.parameters,
    }));
}

/**
 * @param {string} name
 * @returns {ControlModelMember|undefined}
 */
export function getControlModelMember(name) {
    return membersByName.get(name);
}

/**
 * @param {string} name
 * @returns {{ label: string, parameters: { label: string }[] }|null}
 */
export function buildControlModelMemberSignature(name) {
    const member = getControlModelMember(name);
    if (!member || member.kind !== 'function')
        return null;

    return buildSignatureFromParameterNames(
        `controlModel.${name}`,
        member.parameters || [],
    );
}
